import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import styles from './NewPersonPopup.module.css';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { PersonFormValues, personInitialValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<PersonFormValues>();
  const { uiStore } = useStore();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton
        type="save"
        onClick={submitForm}
        disabled={isSubmitting}
        loading={uiStore.isLoading}
      >
        Kaydet
      </CommonButton>
    </>
  );
};

const NewPersonPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore } = useStore();

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isNewPersonOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isNewPersonOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const handleSubmit = async (values: PersonFormValues) => {
    try {
      if (!values.hospitalId) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      const response = await personStore.createPerson(values);
      if (response.error) {
        toast.error('Kişi oluşturulamadı. ' + response.message);
      } else {
        toast.success('Kişi başarıyla oluşturuldu');
        popupStore.setIsNewPersonOpen(false);
      }
    } catch (error) {
      console.error('Kişi oluşturulurken hata:', error);
      toast.error('Kişi oluşturulurken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsNewPersonOpen(false);
    toast.dismiss();
  };

  return (
    <Formik
      initialValues={personInitialValues}
      validationSchema={PersonSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Popup
          isOpen={popupStore.isNewPersonOpen}
          onClose={handleClose}
          title="Yeni Kişi Oluştur"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField name="name" label="İsim" placeholder="İsim giriniz" />
              </div>

              <div className={styles.inputContainer}>
                <FormikDropdown name="hospitalId" options={hospitalOptions} label="Hastane" />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="phoneNumber"
                  label="Telefon Numarası"
                  placeholder="(5XX) XXX XX XX"
                  type="tel"
                  mask={(value: string) => {
                    // Sadece rakamları al
                    const numbers = value.replace(/\D/g, '');
                    // (xxx) xxx xx xx formatına çevir
                    if (numbers.length <= 3) {
                      return numbers.length > 0 ? `(${numbers}` : '';
                    } else if (numbers.length <= 6) {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
                    } else if (numbers.length <= 8) {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
                    } else {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(
                        6,
                        8
                      )} ${numbers.slice(8, 10)}`;
                    }
                  }}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="email"
                  label="E-mail"
                  placeholder="E-mail giriniz (opsiyonel)"
                  type="email"
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(NewPersonPopup);
